import { useState } from 'react';
import { Chip, Text, Alert } from '@nimbus-ds/components';
import { SelectOrEditLink, Stack } from 'commons/components';
import { useModal } from 'commons/hooks';
import {
  SelectProductList,
  SelectedProductInterface,
} from 'domains/Catalog/Products/components';
import { getProductsMaped, getSelectedProductsMaped } from './utils';
import { useTranslationMarketing } from '../hook';

export interface ProductsInterface {
  id: string;
  description: string;
}

interface ProductsProps {
  products: ProductsInterface[];
  title?: string;
  subtitle: string;
  requiredText: string;
  emptyTitle: string;
  emptyText: string;
  ctaText: string;
  showTitle?: boolean;
  onChange: (categories: ProductsInterface[]) => void;
  selectLinkText?: string;
  editLinkText?: string;
  disabled?: boolean;
  limit?: number;
  excludedProductIds?: string[];
}

export function Products({
  products,
  title,
  subtitle,
  requiredText,
  emptyTitle,
  emptyText,
  ctaText,
  showTitle = true,
  onChange,
  selectLinkText,
  editLinkText,
  disabled = false,
  limit,
  excludedProductIds,
}: Readonly<ProductsProps>): JSX.Element {
  const [isShowModal, openModal, closeModal] = useModal();
  const isEmpty = !products.length;
  const [refreshProducts, setRefreshProducts] = useState(false);

  const handleOnChange = (selecteds: SelectedProductInterface[]) => {
    onChange(getProductsMaped(selecteds));
  };

  const openProductsModal = () => {
    setRefreshProducts(true);
    openModal();
  };

  const t = useTranslationMarketing('promotions.settingsPage.products');
  const titleOrDefault = title ?? t('title');

  return (
    <>
      {showTitle && <Text fontWeight="bold">{titleOrDefault}</Text>}
      {!isEmpty && (
        <>
          <Stack spacing="tight" wrap>
            {products.map(({ id, description }) => (
              <Stack.Item key={`${id}`}>
                <Chip
                  key={id}
                  text={description}
                  removable={!disabled}
                  onClick={() =>
                    onChange(products.filter((current) => current.id !== id))
                  }
                />
              </Stack.Item>
            ))}
          </Stack>
          <br />
        </>
      )}
      {!disabled && (
        <>
          <SelectOrEditLink
            isEdit={!isEmpty}
            onSelect={openProductsModal}
            selectLinkText={selectLinkText}
            editLinkText={editLinkText}
          />
          {!!requiredText && <Alert appearance="danger">{requiredText}</Alert>}
          <SelectProductList
            isShow={isShowModal}
            title={title}
            subtitle={subtitle}
            emptyTitle={emptyTitle}
            emptyText={emptyText}
            ctaText={ctaText}
            addItemsToTop
            onClose={closeModal}
            selectedProducts={getSelectedProductsMaped(products)}
            onChange={handleOnChange}
            refreshProducts={refreshProducts}
            setRefreshProducts={setRefreshProducts}
            limit={limit}
            excludedProductIds={excludedProductIds}
          />
        </>
      )}
    </>
  );
}
